import React, { useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useModal } from '../context/ModalContext';
import { adminRepairAttachment, RepairKind } from '../utils/attachmentRepair';
import { formatApiErrorDetail } from '../utils/apiError';
import { ATTACHMENT_ACCEPT, validateAttachmentFile } from '../utils/uploadValidation';

interface AdminAttachmentRepairButtonProps {
  kind: RepairKind;
  targetId: number;
  fileIndex?: number | null;
  onRepaired?: () => void;
  size?: 'sm' | 'lg';
  className?: string;
}

/**
 * Botão para administrador reenviar ficheiro quando o original está ausente (FILE_MISSING).
 */
const AdminAttachmentRepairButton: React.FC<AdminAttachmentRepairButtonProps> = ({
  kind,
  targetId,
  fileIndex,
  onRepaired,
  size = 'sm',
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { showAlert } = useModal();

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const validationError = validateAttachmentFile(file);
    if (validationError) {
      showAlert({
        title: 'Arquivo inválido',
        message: validationError,
        variant: 'warning',
      });
      return;
    }
    try {
      await adminRepairAttachment({ kind, targetId, file, fileIndex });
      showAlert({
        title: 'Anexo corrigido',
        message: 'O ficheiro foi associado com sucesso.',
        variant: 'success',
      });
      onRepaired?.();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: unknown } } };
      const msg = formatApiErrorDetail(
        ax.response?.data?.detail,
        'Não foi possível corrigir o anexo.'
      );
      showAlert({ title: 'Erro ao corrigir', message: msg, variant: 'danger' });
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={ATTACHMENT_ACCEPT}
        onChange={onChange}
      />
      <Button
        type="button"
        variant="outline-warning"
        size={size}
        className={className}
        onClick={() => inputRef.current?.click()}
      >
        Corrigir anexo
      </Button>
    </>
  );
};

export default AdminAttachmentRepairButton;
